import type { StreamProvider } from '@vgtv/api-client';
import { useEffect, useState } from 'preact/hooks';

import { useSchibstedAccount } from '../../../utils/SchibstedAccountProvider';
import { hasStreamProperty } from '../../../utils/player/asset';
import { verifyEidFlowState } from '../services/eid/eidFlow';

import { useCurrentAsset } from './CurrentAssetProvider';
import { useGeolocation } from './GeolocationProvider';

const verifyEidByUserSignature = async (sig: string) => {
  const response = await fetch('https://tv.vg.no/service/account/eid/status', {
    credentials: 'omit',
    headers: { 'x-sig': sig },
  });

  return response.json();
};

export const useAccountVerification = (provider: StreamProvider) => {
  const { getSessionSignature } = useSchibstedAccount();
  const { location } = useGeolocation();

  // Required eID (BankID) verification if outside of Norway/Sweden but in the European Economic Area
  const [isVerificationRequired, setIsVerificationRequired] = useState(false);
  const { asset } = useCurrentAsset();

  const isAssetGeoblocked = hasStreamProperty(asset, 'geoblocked');

  useEffect(() => {
    if (provider !== 'ab') {
      return;
    }

    if (!isAssetGeoblocked || !location.eea || location.local) {
      setIsVerificationRequired(false);

      return;
    }

    if (asset?.id) {
      const result = verifyEidFlowState(asset.id);

      setIsVerificationRequired(!result);
    }
  }, [provider, isAssetGeoblocked, location, asset?.id]);

  useEffect(() => {
    if (provider !== 'vgtv') {
      return;
    }

    if (
      !isAssetGeoblocked ||
      !location.eea ||
      location.local ||
      // Takk for alt, embedded on aftonbladet.se
      (location.se && asset?.id === 280940)
    ) {
      setIsVerificationRequired(false);

      return;
    }

    (async () => {
      try {
        const sig = await getSessionSignature();

        if (!sig) {
          // This means the user is not logged in
          return;
        }

        const data = await verifyEidByUserSignature(sig);

        setIsVerificationRequired(!data.verified);
      } catch (err) {
        console.error(err);
        setIsVerificationRequired(true);
      }
    })();
  }, [asset?.id, provider, isAssetGeoblocked, getSessionSignature, location]);

  return {
    isVerificationRequired,
  };
};
